<template>
  <form class="relative" @submit="onSubmit">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="flex flex-col space-y-4">
        <BaseCard>
          <h3 class="mb-3 text-sm text-background-fg/70">
            Business
          </h3>
          <div class="space-y-2">
            <FormInputText
              name="tpReference"
              label="Reference"
            />
            <FormInputText
              name="businessName"
              label="Business Name"
            />
            <FormInputText
              name="merchantCategoryCode"
              label="Merchant Category Code (MCC)"
              pattern="[0-9]{4}"
              inputmode="numeric"
            />
            <FormInputSelect
              name="currency"
              label="Currency"
              :options="merchantCurrencyCodeOptions"
              disable-null
              :disabled="currencyFieldDisabled"
              :warning="
                currencyFieldDisabled
                  ? 'Cannot edit currency after a merchant has created transactions'
                  : undefined
              "
            />
            <FormInputText
              name="addressLine1"
              label="Address Line 1"
            />
            <FormInputText
              name="addressLine2"
              label="Address Line 2"
            />
          </div>
        </BaseCard>

        <BaseCard>
          <h3 class="mb-3 text-sm text-background-fg/70">
            Customer
          </h3>
          <div class="space-y-2">
            <FormInputText
              name="customerName"
              label="Customer Name"
            />
            <FormInputText
              name="customerEmail"
              label="Customer Email"
            />
            <FormInputText
              name="customerPhone"
              label="Customer Phone"
            />
          </div>
        </BaseCard>
      </div>
      <div class="flex flex-col space-y-4">
        <BaseCard>
          <h3 class="mb-3 text-sm text-background-fg/70">
            Advanced
          </h3>
          <div class="space-y-2">
            <FormInputCheckbox
              name="isDemoMerchant"
              label="Is this a demo merchant?"
            />
            <FormInputSelect
              v-if="isMenuPresetAvailable"
              name="demoMenuId"
              label="Preloaded product set"
              :options="menuPresetOptions"
            />
            <FormInputCheckbox
              name="isFromBackbook"
              label="Is this merchant from the backbook?"
            />
            <FormInputCheckbox
              name="isActive"
              label="Is this merchant active?"
            />
          </div>
        </BaseCard>
        <BaseCard
          v-if="
            organizationConfig?.beepaidGoEnabled ||
            organizationConfig?.orderAndBeepaidEnabled ||
            organizationConfig?.donationsEnabled
          "
        >
          <h3 class="mb-3 text-sm text-background-fg/70">
            Modules
          </h3>
          <div class="space-y-2">
            <FormInputCheckbox
              v-if="organizationConfig?.beepaidGoEnabled"
              name="hasBeepaidGoModule"
              label="Beepaid GO"
            />
            <FormInputCheckbox
              v-if="
                organizationConfig?.orderAndBeepaidEnabled
              "
              name="hasBeepaidOrderModule"
              label="Order & Beepaid"
              disabled
            />
            <FormInputCheckbox
              name="hasSalesModule"
              label="Takepayments+ Sales Module"
            />
            <FormInputCheckbox
              v-if="organizationConfig?.donationsEnabled"
              name="hasDonationsModule"
              label="Takepayments+ Donations Module"
            />
          </div>
        </BaseCard>

        <BaseCard
          v-if="
            organizationConfig?.beepaidGoEnabled ||
            organizationConfig?.orderAndBeepaidEnabled
          "
          class="relative"
        >
          <h3 class="mb-3 text-sm text-background-fg/70">
            Cardstream
          </h3>
          <div class="space-y-2">
            <FormInputText
              :disabled="!areCardstreamSettingsEnabled"
              name="cardstreamMerchantId"
              label="Cardstream Merchant ID"
              pattern="[0-9]+"
              inputmode="numeric"
            />
            <FormInputText
              :disabled="!areCardstreamSettingsEnabled"
              name="cardstreamMerchantSecret"
              label="Cardstream Merchant Secret"
            />
          </div>
          <template v-if="!areCardstreamSettingsEnabled">
            <div
              class="absolute inset-0 bg-background-fg/3"
            />
          </template>
        </BaseCard>

        <BaseCard class="relative">
          <h3 class="mb-3 text-sm text-background-fg/70">
            Acquirer
          </h3>
          <div class="space-y-2">
            <FormInputSelect
              name="acquirer"
              label="Acquirer"
              :options="acquirerOptions"
              disable-null
            />
            <FormInputText
              :disabled="!arePaxSettingsEnabled"
              name="paxMerchantId"
              label="Acquirer Merchant ID (A920) or NAV Site Reference (DX8000)"
            />
            <FormInputCheckbox
              v-if="values.acquirer === 'evo'"
              name="evoGratuityIsAvailable"
              label="Gratuity is available"
            />
          </div>
          <template v-if="!arePaxSettingsEnabled">
            <div
              class="absolute inset-0 bg-background-fg/3"
            />
          </template>
        </BaseCard>
      </div>
    </div>
    <div class="flex justify-end">
      <FormSubmitButton :is-loading="isSubmitting">
        {{ merchantId ? "Update" : "Create" }} Merchant
      </FormSubmitButton>
    </div>

    <BaseInnerLoading v-if="isRetrievingMerchant" />

    <BaseConfirmation
      ref="deactivateConfirmationRef"
      title="Deactivate Merchant"
      description="Are you sure you want to deactivate this merchant? Doing so will prevent this merchant from accessing their app or web portal."
      confirm-label="Yes"
      cancel-label="No"
    />
  </form>
</template>

<script setup lang="ts">
import type { Acquirer } from "@/services/sharedTypes/merchants"
import {
  type CreateEditMerchantFormOutput,
  useCreateEditMerchantForm,
} from "@/composables/forms/useCreateEditMerchantForm"
import type { SelectOption } from "./InputSelect.vue"

const props = defineProps<{
  merchantId?: number
}>()
const router = useRouter()

defineEmits<{
  (
    event: "submit",
    values: CreateEditMerchantFormOutput
  ): void
}>()

const { organizationConfig, defaultMerchantCurrency } =
  useOrganizationConfig()

const {
  handleSubmit,
  values,
  setErrors,
  setValues,
  setFieldValue,
  isSubmitting,
} = useCreateEditMerchantForm(defaultMerchantCurrency.value)

const isRetrievingMerchant = ref(false)
const deactivateConfirmationRef = useTemplateRef(
  "deactivateConfirmationRef"
)

const menuPresetOptions = ref<Array<SelectOption<string>>>(
  []
)
const hasManuallyChangedCurrency = ref(false)
const isSyncingDefaultCurrency = ref(false)

watch(
  () => values.isActive,
  async (newValue, oldValue) => {
    if (oldValue && !newValue) {
      if (deactivateConfirmationRef.value === null) return

      const { isCanceled } =
        await deactivateConfirmationRef.value.reveal()
      if (isCanceled) {
        setFieldValue("isActive", true)
      }

      return
    }
  }
)

watch(
  () => values.currency,
  (newValue, oldValue) => {
    if (
      props.merchantId !== undefined ||
      isSyncingDefaultCurrency.value ||
      oldValue === undefined ||
      newValue === oldValue
    ) {
      return
    }

    hasManuallyChangedCurrency.value = true
  }
)

watch(
  defaultMerchantCurrency,
  (newCurrency, oldCurrency) => {
    if (
      props.merchantId !== undefined ||
      hasManuallyChangedCurrency.value ||
      newCurrency === oldCurrency
    ) {
      return
    }

    isSyncingDefaultCurrency.value = true
    setFieldValue("currency", newCurrency)
    nextTick(() => {
      isSyncingDefaultCurrency.value = false
    })
  }
)

const currencyFieldDisabled = ref(false)
onMounted(async () => {
  try {
    const presets = await listMenuPresets()
    menuPresetOptions.value = presets.map(
      ({ id, title }) => ({
        label: title,
        value: id,
      })
    )
  } catch {
    toast.error("Failed to load menu presets")
  }

  if (props.merchantId !== undefined) {
    try {
      isRetrievingMerchant.value = true
      const { id, canEditCurrency, ...merchant } =
        await retrieveMerchantForUpdate(props.merchantId)
      setValues(merchant)
      currencyFieldDisabled.value = !canEditCurrency
      setFieldValue(
        "evoGratuityIsAvailable",
        merchant.evoConfig.gratuityIsAvailable
      )
    } catch {
      toast.error("Merchant not found")
      router.push({
        name: "merchantIndex",
      })
    } finally {
      isRetrievingMerchant.value = false
    }
  }
})

const onSubmit = handleSubmit(async (formValues) => {
  try {
    if (props.merchantId) {
      await updateMerchant(props.merchantId, {
        ...formValues,
        evoConfig: {
          gratuityIsAvailable:
            formValues.evoGratuityIsAvailable,
        },
      })
      toast.success("Merchant updated")

      router.push({
        name: "merchantDetail",
        params: {
          tenantId: props.merchantId,
        },
      })
    } else {
      await createMerchant({
        ...formValues,
        evoConfig: {
          gratuityIsAvailable:
            formValues.evoGratuityIsAvailable,
        },
      })

      toast.success("Merchant created")

      router.push({ name: "merchantIndex" })
    }
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.merchantId
        ? "Failed to update merchant"
        : "Failed to create merchant"
    )
    throw error
  }
})

const arePaxSettingsEnabled = computed(
  () => values.hasSalesModule || values.hasDonationsModule
)
const areCardstreamSettingsEnabled = computed(
  () =>
    values.hasBeepaidGoModule ||
    values.hasBeepaidOrderModule
)

const acquirerOptions: Array<SelectOption<Acquirer>> = [
  {
    label: "Barclaycard/Elavon",
    value: "barclaycard-elavon",
  },
  { label: "Evo", value: "evo" },
]

const isMenuPresetAvailable = computed(
  () =>
    !props.merchantId &&
    values.isDemoMerchant &&
    values.hasSalesModule
)

watch(isMenuPresetAvailable, (value) => {
  if (!value) {
    setFieldValue("demoMenuId", null)
  }
})
</script>
